import type * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { speechTokenService } from './speechTokenService';

/** Recognition settings for a browser live-mic run (resolved server-side, agent config included). */
export interface BrowserRecognitionSettings {
  /** Recognition language, e.g. "en-US". */
  language: string;
  /** Whether partial results should carry speaker ids during streaming recognition. */
  diarizeIntermediateResults?: boolean;
  /** Phrase list biasing recognition toward domain vocabulary (from the agent's Speech config). */
  phrases?: string[];
}

export interface BrowserRecognitionEvents {
  onPartial: (text: string, speakerId?: string | null) => void;
  onFinal: (text: string, speakerId?: string | null) => void;
  /** Unrecoverable recognition error (network, auth); recognition has stopped. */
  onError: (message: string) => void;
}

/** Refresh the ~10-minute STS token with margin so long sessions never hit expiry. */
const TOKEN_REFRESH_INTERVAL_MS = 8 * 60 * 1000;

/**
 * Live microphone recognition in the browser using the Azure Speech JS SDK. Auth uses short-lived
 * tokens from the API (`POST /v1/audio/speech-token`) — the Speech subscription key never reaches
 * the browser. The SDK is imported lazily so the playground does not grow the initial bundle.
 *
 * One instance per recognition run; create a new one for each session.
 */
export class BrowserSpeechRecognition {
  private transcriber: SpeechSDK.ConversationTranscriber | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;

  /** Requests mic access and starts continuous transcription. Rejects when startup fails. */
  async start(settings: BrowserRecognitionSettings, events: BrowserRecognitionEvents): Promise<void> {
    const sdk = await import('microsoft-cognitiveservices-speech-sdk');

    const { token, region, endpoint } = await speechTokenService.getToken();

    // Region-based auth is the common case; a custom endpoint uses fromEndpoint with the token
    // applied through the authorizationToken setter (empty key keeps the key server-side).
    const speechConfig = region
      ? sdk.SpeechConfig.fromAuthorizationToken(token, region)
      : sdk.SpeechConfig.fromEndpoint(new URL(endpoint!), '');
    if (!region) {
      speechConfig.authorizationToken = token;
    }

    speechConfig.speechRecognitionLanguage = settings.language || 'en-US';

    if (settings.diarizeIntermediateResults) {
      speechConfig.setProperty(
        sdk.PropertyId.SpeechServiceResponse_DiarizeIntermediateResults,
        'true',
      );
    }

    const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
    const transcriber = new sdk.ConversationTranscriber(speechConfig, audioConfig);

    // Bias recognition toward the agent's domain vocabulary. The JS SDK has no per-list weight
    // setter (unlike the server SDK), so only the phrases themselves apply here.
    if (settings.phrases?.length) {
      const grammar = sdk.PhraseListGrammar.fromRecognizer(transcriber);
      for (const phrase of settings.phrases.slice(0, 500)) {
        if (phrase?.trim()) grammar.addPhrase(phrase.trim());
      }
    }

    transcriber.transcribing = (_, e) => {
      if (e.result?.text) {
        events.onPartial(e.result.text, e.result.speakerId ?? null);
      }
    };

    transcriber.transcribed = (_, e) => {
      if (e.result?.reason === sdk.ResultReason.RecognizedSpeech && e.result.text) {
        events.onFinal(e.result.text, e.result.speakerId ?? null);
      }
    };

    transcriber.canceled = (_, e) => {
      if (e.reason === sdk.CancellationReason.Error && !this.stopped) {
        events.onError(e.errorDetails || 'Speech recognition was canceled by the service.');
      }
    };

    await new Promise<void>((resolve, reject) => {
      transcriber.startTranscribingAsync(resolve, (error) => reject(new Error(error)));
    });

    this.transcriber = transcriber;

    // STS tokens expire after ~10 minutes; refresh via the setter so hour-long sessions survive.
    this.refreshTimer = setInterval(() => {
      void speechTokenService
        .getToken()
        .then((fresh) => {
          if (this.transcriber) this.transcriber.authorizationToken = fresh.token;
        })
        .catch(() => {
          // A missed refresh is retried on the next tick; the current token may still be valid.
        });
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  /** Stops recognition, letting the trailing utterance flush as a final before resolving. */
  async stop(): Promise<void> {
    this.stopped = true;

    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    const transcriber = this.transcriber;
    this.transcriber = null;
    if (!transcriber) return;

    try {
      await new Promise<void>((resolve, reject) => {
        transcriber.stopTranscribingAsync(resolve, (error) => reject(new Error(error)));
      });
    } finally {
      transcriber.close();
    }
  }
}
